import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Colorize } from "@material-ui/icons";
import { ColorBox } from 'material-ui-color';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { IconButton, InputAdornment, FormControlLabel } from "@material-ui/core";
import Checkbox from '@material-ui/core/Checkbox';

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},
	// ✅ ESTILOS MEJORADOS PARA EL FORMULARIO
	formField: {
		marginBottom: theme.spacing(2),
	},
	formFieldLabel: {
		fontWeight: "bold",
		color: "#1976d2",
		marginBottom: theme.spacing(1),
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	colorAdorment: {
		width: 20,
		height: 20,
		borderRadius: "50%",
		border: `2px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
	},
	// ✅ CORREGIDO: KanbanSection dinámico según tema
	kanbanSection: {
		backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
		padding: theme.spacing(2),
		borderRadius: theme.spacing(1),
		marginTop: theme.spacing(2),
		border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
	},
	kanbanLabel: {
		fontWeight: "bold",
		color: theme.palette.primary.main,
	},
	// ✅ CORREGIDO: ColorPickerSection dinámico según tema
	colorPickerSection: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: theme.mode === 'dark' ? '#374151' : '#fafafa',
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
	},
	// ✅ CORREGIDO: TextField dinámico según tema
	textField: {
		'& .MuiOutlinedInput-root': {
			backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
			color: theme.palette.text.primary,
			'&:hover': {
				backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
			},
			'&.Mui-focused': {
				backgroundColor: theme.mode === 'dark' ? '#374151' : 'white',
				boxShadow: `0 0 0 2px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`,
			},
			"& fieldset": {
				borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
			},
			"&:hover fieldset": {
				borderColor: theme.palette.primary.main,
			},
			"&.Mui-focused fieldset": {
				borderColor: theme.palette.primary.main,
			},
		},
		"& .MuiInputLabel-root": {
			color: theme.palette.text.secondary || theme.palette.text.primary,
		},
		"& .MuiInputLabel-root.Mui-focused": {
			color: theme.palette.primary.main,
		},
		"& .MuiFormHelperText-root": {
			color: theme.palette.text.secondary || theme.palette.text.primary,
		},
	},
}));

const TagSchema = Yup.object().shape({
	name: Yup.string()
		.min(3, "Mensagem muito curta")
		.required("Obrigatório")
});

const TagModal = ({ open, onClose, tagId, reload }) => {
	const classes = useStyles();
	const theme = useTheme();
	const { user } = useContext(AuthContext);
	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

	const initialState = {
		name: "",
		color: "",
		kanban: 0
	};

	const [tag, setTag] = useState(initialState);
	const [ kanban, setKanban] = useState(0);

	useEffect(() => {
		try {
			(async () => {
				if (!tagId) return;

				const { data } = await api.get(`/tags/${tagId}`);
				setKanban(data.kanban);
				setTag(prevState => {
					return { ...prevState, ...data };
				});
			})()
		} catch (err) {
			toastError(err);
		}
	}, [tagId, open]);

	const handleClose = () => {
		setTag(initialState);
		setColorPickerModalOpen(false);
		onClose();
	};

	const handleKanbanChange = (e) => {
		setKanban( e.target.checked ? 1 : 0);
	};

	const handleSaveTag = async values => {
		const tagData = { ...values, userId: user.id, kanban };
		try {
			if (tagId) {
				await api.put(`/tags/${tagId}`, tagData);
			} else {
				await api.post("/tags", tagData);
			}
			toast.success(i18n.t("tagModal.success"));
			if (typeof reload == 'function') {
				reload();
			}
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
				PaperProps={{
					style: {
						backgroundColor: theme.palette.background.paper,
						border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
					},
				}}
			>
				<DialogTitle id="form-dialog-title" style={{ backgroundColor: theme.palette.primary.main, color: 'white' }}>
					{(tagId ? `${i18n.t("tagModal.title.edit")}` : `${i18n.t("tagModal.title.add")}`)}
				</DialogTitle>
				<Formik
					initialValues={tag}
					enableReinitialize={true}
					validationSchema={TagSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveTag(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers style={{ backgroundColor: theme.palette.background.paper }}>
								{/* ✅ CAMPO NOMBRE */}
								<div className={classes.formField}>
									<TextField
										label={i18n.t("tagModal.form.name")}
										name="name"
										value={values.name || ""}
										onChange={(e) => {
											setFieldValue('name', e.target.value);
											setTag(prev => ({ ...prev, name: e.target.value }));
										}}
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
										placeholder="Ej: Atención, Cliente, Interesado..."
										className={classes.textField}
									/>
								</div>

								{/* ✅ CAMPO COLOR */}
								<div className={classes.formField}>
									<TextField
										fullWidth
										label={i18n.t("tagModal.form.color")}
										name="color"
										id="color"
										value={values.color || ""}
										onChange={(e) => {
											setFieldValue('color', e.target.value);
											setTag(prev => ({ ...prev, color: e.target.value }));
										}}
										error={touched.color && Boolean(errors.color)}
										helperText={touched.color && errors.color}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<div
														style={{ backgroundColor: values.color || "#1976d2" }}
														className={classes.colorAdorment}
													></div>
												</InputAdornment>
											),
											endAdornment: (
												<IconButton
													size="small"
													color="primary"
													onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}
													title="Seleccionar color"
												>
													<Colorize style={{ color: theme.palette.primary.main }} />
												</IconButton>
											),
										}}
										variant="outlined"
										margin="dense"
										placeholder="#1976d2"
										className={classes.textField}
									/>
								</div>

								{/* ✅ SECCIÓN KANBAN */}
								{(user.profile === "admin" || user.profile === "supervisor") && (
									<div className={classes.kanbanSection}>
										<FormControlLabel
											control={
												<Checkbox
													checked={kanban === 1}
													onChange={handleKanbanChange}
													value={kanban}
													color="primary"
												/>
											}
											label={
												<span className={classes.kanbanLabel}>
													Usar en Kanban
												</span>
											}
										/>
										<p style={{ margin: "8px 0 0 0", fontSize: "0.875rem", color: theme.palette.text.secondary || theme.palette.text.primary }}>
											Las etiquetas con Kanban activado aparecerán como columnas en el tablero Kanban
										</p>
									</div>
								)}

								{/* ✅ COLOR PICKER */}
								{colorPickerModalOpen && (
									<div className={classes.colorPickerSection}>
										<ColorBox
											disableAlpha={true}
											hslGradient={false}
											style={{ margin: '0 auto' }}
											value={tag.color}
											onChange={val => {
												setTag(prev => ({ ...prev, color: `#${val.hex}` }));
											}}
										/>
									</div>
								)}
							</DialogContent>
							<DialogActions style={{ 
								backgroundColor: theme.palette.background.paper,
								borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
							}}>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
									style={{ color: theme.palette.error.main }}
								>
									{i18n.t("tagModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{tagId
										? `${i18n.t("tagModal.buttons.okEdit")}`
										: `${i18n.t("tagModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default TagModal;
